'use client';

import { Radio } from '@base-ui/react/radio';
import clsx from 'clsx';
import type { ReactNode } from 'react';

import { DataTestIds } from '@repo/frontend-shared';

import '@repo/sds/components/SDSRadio/SDSRadio.scss';

export interface SDSRadioProps {
  value: string;
  checked?: boolean; // For controlled usage outside of RadioGroup
  isDisabled?: boolean;
  isError?: boolean;
  children?: ReactNode; // Content/label for the radio
  className?: string;
  id?: string;
  name?: string; // Groups standalone radios so only one can be checked at a time
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  dataTestId?: string;
  // Event handlers for standalone usage
  onChange?: (checked: boolean) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

export function SDSRadio({
  value,
  checked,
  isDisabled = false,
  isError = false,
  children,
  className,
  id,
  name,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  dataTestId,
  onChange,
  onFocus,
  onBlur,
}: SDSRadioProps) {
  const radioClass = clsx(
    'SDSRadio',
    {
      'SDSRadio--active': checked,
      'SDSRadio--disabled': isDisabled,
      'SDSRadio--error': isError,
    },
    className,
  );

  // For standalone usage (not within RadioGroup)
  if (onChange) {
    return (
      <div className={radioClass}>
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          disabled={isDisabled}
          onChange={e => onChange(e.target.checked)}
          onFocus={onFocus}
          onBlur={onBlur}
          className="SDSRadio__input"
          id={id}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          data-testid={dataTestId || DataTestIds.SDS.SDS_RADIO}
        />
        <div className="SDSRadio__indicator" />
        {children && (
          <label htmlFor={id} className="SDSRadio__label">
            {children}
          </label>
        )}
      </div>
    );
  }

  // For usage within RadioGroup (recommended)
  return (
    <label className={radioClass} htmlFor={id}>
      <Radio.Root
        value={value}
        disabled={isDisabled}
        className="SDSRadio__item"
        id={id}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        onFocus={onFocus}
        onBlur={onBlur}
        data-testid={dataTestId || DataTestIds.SDS.SDS_RADIO}
      >
        <Radio.Indicator className="SDSRadio__indicator" />
      </Radio.Root>
      {children && <span className="SDSRadio__label">{children}</span>}
    </label>
  );
}
